<template>
  <div class="page-container">
    <h1>Port calls</h1>

    <b-card no-body class="d-flex content-center">
      <b-card-body>
        <port-search-widget
          title="Port search"
          :search-results="port.results"
          :search-value="port.value"
          @input="applyFilter"/>
      </b-card-body>
    </b-card>

    <b-row>
      <b-col lg="12" class="d-flex">
        <port-calls-table
          :items="portCalls.items"
          :sorting="allPortCallsLoaded"
          :current-page="portCalls.currentPage"
          :export-types="exportTypes"
          @page-changed="tablePageChanged('portCalls', ...arguments)"
          @export="format => handleExport('portCalls', format)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import cloneDeep from 'lodash-es/cloneDeep';
import getNested from 'lodash-es/get';
import { mapActions, mapGetters } from 'vuex';
import { downloadAsFileByURL } from '@/shared/download-utils';
import { defaultPortSearchResults } from '@/shared/constants/ports';
import PortCallsTable from '@/components/vesseldetails/PortCallsTable';
import PortSearchWidget from '@/components/portscomparison/PortSearchWidget';
import moment from 'moment';

export default {
  name: 'port-calls',

  components: {
    PortSearchWidget,
    PortCallsTable,
  },

  data() {
    return {
      exportTypes: ['csv'],

      port: {
        results: defaultPortSearchResults,
        value: null,
      },

      portCalls: {
        items: [],
        apiItemsPerPage: 100, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or max_page, it doesn't matter since pages are starts from '1'
        apiCurrentPage: null,
        currentPage: null,
        method: this.getPortCalls,
      },
    }
  },

  methods: {
    ...mapActions('api/tradeflows', ['getPortCalls']),

    async applyFilter(event) {
      this.resetDashboardData();
      this.port.value = getNested(event, 'value', null);
      this.port.results = getNested(event, 'results', null);

      if(this.port.value) {
        await this.fetchTableItems(
          'portCalls', {
            ...this.apiFilterValues,
            limit: this.portCalls.apiItemsPerPage,
        });
      }
    },

    resetDashboardData() {
      Object.assign(this.portCalls, {
        items: [],
        apiCurrentPage: null,
        apiTotalPages: null,
        currentPage: null,
      });
    },

    async tablePageChanged(sectionName, currentPage, totalPages) {
      const section = this[sectionName];

      // table on demand loading, overview:
      // 1. detect when a current page is less than 'preloadPagesRange' value
      // 2. add items which are received by 'preloadRequestsCount' requests
      if(this.getActiveRequestsCount) {
        return; // prevent multiple requests
      }

      // calculate an API page number to start
      const apiPagesLoaded = section.items.length / section.apiItemsPerPage;
      const preloadPagesRange = 3;

      if(
        totalPages - currentPage < preloadPagesRange &&
        Number.isInteger(apiPagesLoaded)
      ) {
        const parameters = {
          ...this.apiFilterValues,
          limit: section.apiItemsPerPage,
          page: apiPagesLoaded + 1,
        };
        await this.fetchTableItems(sectionName, parameters);
        // b-pagination resets a current page when data is changed we have to restore it this way
        // https://github.com/bootstrap-vue/bootstrap-vue/issues/3372
        section.currentPage = currentPage;
      }
    },

    async fetchTableItems(sectionName, filterParameters, requestsCount = 3) {
      let countdown = requestsCount; // no more than countdown requests
      let parameters = cloneDeep(filterParameters);
      let response = null;
      const section = this[sectionName];

      do {
        response = await section.method(parameters);
        const responseData = getNested(response, 'data.data', []);

        if(responseData) {
          section.items.push.apply(section.items, responseData);
          section.apiTotalPages = getNested(response, 'data.maxPage', 0);
          section.apiCurrentPage = getNested(response, 'data.currentPage', 0);
          parameters.page += 1;
        } else {
          break;
        }
        countdown--;
      } while (parameters.page <= response.data.maxPage && countdown);
    },

    async handleExport(sectionName, format) {
      const limit = 20000;
      let response = await this.getPortCalls({...this.apiFilterValues, format, limit});

      console.log('response csv', getNested(response, 'data.csv', null), `port-calls-export_${timestamp}.${format}`);

      const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      downloadAsFileByURL(
        getNested(response, 'data.csv', null),
        `port-calls-export_${timestamp}.${format}`
      );
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),

    apiFilterValues() {
      return {
        port_id: getNested(this.port, 'value.portId', null),
      };
    },

    allPortCallsLoaded() {
      return (
        this.portCalls.apiTotalPages &&
        this.portCalls.apiCurrentPage &&
        this.portCalls.apiTotalPages === this.portCalls.apiCurrentPage
      );
    },
  },
}
</script>

<style scoped lang="scss">

</style>
